import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button, notification } from 'antd';

import { EMPTY_MESSAGES } from '@/shared/lib/constants';
import { EmptyStateWithAction } from '@/shared/ui/EmptyStateWithAction';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';

import { useFilterStore } from '@/entities/discovery';
import { useSearchStore } from '@/entities/search';

import { useSearch } from '@/features/search/model';
import { getUniqueKey } from '@/features/search/model/_internal/cacheManager';

import { SearchPreviewPanel } from '@/widgets/search/SearchPreviewPanel';
import { SearchResults } from '@/widgets/search/SearchResults';

import { useSearchResultsContainer } from './model.ts';
import styles from './SearchResultsContainer.module.css';

export const SearchResultsContainer: React.FC = () => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    fetchStatus,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    searchQueryKey,
  } = useSearch();
  const prevErrorRef = useRef<Error | null>(null);
  const filters = useFilterStore((state) => state.filters);
  const searchQuery = useSearchStore((state) => state.query);
  const setSortBy = useSearchStore((state) => state.setSortBy);

  const listResetKey = useMemo(() => JSON.stringify(searchQueryKey), [searchQueryKey]);

  const {
    allResults,
    totalCount,
    query,
    selectedResult,
    setSelectedResult,
    infoRows,
    sections,
    description,
    isPreviewLoading,
    isPreviewError,
    refetchPreview,
    handleLoadMore,
  } = useSearchResultsContainer({
    data,
    hasNextPage,
    fetchNextPage,
  });

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'tagsOperator') return false;
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    useFilterStore.getState().clearAllFilters();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setSortBy('lastUpdated');
    }
  }, [searchQuery, setSortBy]);

  useEffect(() => {
    if (isError && error && prevErrorRef.current !== error) {
      prevErrorRef.current = error as Error;

      if (allResults.length > 0) {
        notification.error({
          key: 'filter-apply-error',
          message: '필터 적용 실패',
          description: '필터 적용에 실패했습니다. 다시 시도해 주세요.',
          duration: 10,
          placement: 'bottomLeft',
          btn: (
            <Button
              size="small"
              type="primary"
              onClick={() => {
                notification.destroy('filter-apply-error');
                refetch();
              }}>
              재시도
            </Button>
          ),
        });
      }
    }
  }, [isError, error, refetch, allResults.length]);

  const emptyContent = useMemo(() => {
    if ((isError || fetchStatus === 'paused') && allResults.length === 0) {
      return (
        <ErrorFallback
          error={error}
          description="검색에 실패했습니다. 다시 시도해 주세요."
          resetErrorBoundary={refetch}
        />
      );
    }

    if (allResults.length === 0) {
      let message: string;
      let actionLabel: string | undefined;
      let onAction: (() => void) | undefined;

      if (hasActiveFilters) {
        message = EMPTY_MESSAGES.NO_FILTER_RESULTS;
        actionLabel = '필터 초기화';
        onAction = handleResetFilters;
      } else if (searchQuery) {
        message = `"${searchQuery}"에 대한 검색 결과가 없습니다`;
        actionLabel = undefined;
        onAction = undefined;
      } else {
        message = EMPTY_MESSAGES.NO_DATA;
        actionLabel = undefined;
        onAction = undefined;
      }

      return (
        <EmptyStateWithAction
          message={message}
          actionLabel={actionLabel}
          onAction={onAction}
          className={styles.empty}
        />
      );
    }

    return null;
  }, [isError, allResults.length, fetchStatus, error, refetch, hasActiveFilters, searchQuery, handleResetFilters]);

  return (
    <div className={styles.resultsContainer}>
      <SearchResults
        listResetKey={listResetKey}
        results={allResults}
        query={query}
        totalCount={totalCount}
        isFetching={isFetching}
        isLoading={isLoading}
        onLoadMore={handleLoadMore}
        hasMore={hasNextPage}
        selectedId={selectedResult ? getUniqueKey(selectedResult) : null}
        onSelectResult={setSelectedResult}
        showResultCount={false}
        headerRight={<p className={styles.resultCount}>총 {totalCount.toLocaleString()}개의 결과</p>}
        emptyContent={emptyContent}
        previewPanel={
          <SearchPreviewPanel
            selectedResult={selectedResult}
            infoRows={infoRows}
            sections={sections}
            description={description}
            isLoading={isPreviewLoading}
            isError={isPreviewError}
            onRetry={refetchPreview}
          />
        }
      />
    </div>
  );
};
